import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { Loader2 } from "lucide-react"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-darkBlue text-white shadow hover:bg-midnightBlue",
        default2:
          "bg-darkBlue text-white shadow hover:bg-midnightBlue",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-darkBlue bg-background text-darkBlue shadow-sm hover:bg-midnightBlue hover:text-white",
        cancel:
          "border border-darkBlue bg-darkBlue text-white shadow-sm hover:bg-midnightBlue",
        secondary:
          "bg-darkBlue text-white shadow-sm hover:bg-midnightBlue",
        ghost: "text-darkBlue hover:bg-midnightBlue hover:text-white",
        link: "text-darkBlue underline-offset-4 hover:underline",
        info: "bg-darkBlue text-white shadow hover:bg-midnightBlue",

      },
      size: {
        default: "h-9 px-4 py-2",
        default2: "px-2 py-0.5",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

// Quick actions finish before this, so they never flash a spinner
const SPINNER_DELAY_MS = 250

const isPromiseLike = (value: unknown): value is PromiseLike<unknown> =>
  !!value && typeof (value as PromiseLike<unknown>).then === "function"

/**
 * When onClick returns a promise (an async handler), the button stays busy until it settles:
 * further clicks are ignored, and after a short delay it is disabled with a spinner.
 * If the page already disables the button for its own loading state, only the click guard applies,
 * so its own "Saving..." style text is not doubled up with a spinner.
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, onClick, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    const [isBusy, setIsBusy] = React.useState(false)
    const [showSpinner, setShowSpinner] = React.useState(false)
    const busyRef = React.useRef(false)
    const mountedRef = React.useRef(true)

    React.useEffect(() => {
      mountedRef.current = true
      return () => { mountedRef.current = false }
    }, [])

    const handleClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
      if (busyRef.current) {
        event.preventDefault()
        return
      }
      const result: unknown = onClick?.(event)
      if (!isPromiseLike(result)) return

      busyRef.current = true
      setIsBusy(true)
      const spinnerTimer = window.setTimeout(() => {
        if (mountedRef.current && busyRef.current) setShowSpinner(true)
      }, SPINNER_DELAY_MS)
      const finish = () => {
        window.clearTimeout(spinnerTimer)
        busyRef.current = false
        if (!mountedRef.current) return
        setIsBusy(false)
        setShowSpinner(false)
      }
      result.then(finish, finish)
    }, [onClick])

    // asChild renders the caller's own element, so leave its behaviour untouched
    if (asChild) {
      return (
        <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} onClick={onClick} disabled={disabled} {...props}>
          {children}
        </Comp>
      )
    }

    const spinner = showSpinner && !disabled ? <Loader2 aria-hidden className="animate-spin" /> : null
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        onClick={handleClick}
        disabled={disabled || showSpinner}
        aria-busy={isBusy || undefined}
        {...props}
      >
        {/* Icon-only buttons swap their icon for the spinner; others show it before the label */}
        {spinner && size === "icon" ? spinner : <>{spinner}{children}</>}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
